import React, { useRef, useState } from "react";
import { Button } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    errorMessage,
    formatAmount,
    formatDate,
    formatStatus,
    formatUsername,
    permissionAllow,
    transactionCat,
    transactionType,
    ucFirst,
    getTransactionBalanceAfter,
    getTransactionBalanceBefore,
} from "../../../util/Helpers";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import cogoToast from "cogo-toast";
import { useSubmitAccountsHistoryExportMutation } from "../../../../redux/apiSlice";
import { FilterGroup } from "../../Common/components/FilterGroup";
import { useQuery } from "../../../util/Hooks";
import { Permissions, PermissionType } from "../../../util/Permissions";
import { useSelector } from "react-redux";
import ViewTransactionModal from "../../UserManagement/pages/selected-user/view-user/user-pages/account-information/account-pages/transactions/modal/ViewTransactionModal";
import { Permission } from "../../Common/layout/Permission"
import { PARTNERS } from "../../../util/constants/Partners";

const getExportErrorMessage = (error) => (
    error?.data
    || error?.error
    || error?.message
    || errorMessage(error)
    || "Transactions export failed"
);

export const TransactionTable = ({ category, userGroup }) => {

    const query = useQuery();
    const tableRef = useRef();
    const { type } = JSON.parse(query.get('filters') ?? "{\"type\":\"live\"}");
    const adminUser = useSelector(state => state.auth.user);
    /* Transaction Modal State Management */
    const [transactionModal, setTransactionModal] = useState({ showViewTransactionModal: false, details: null });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setTransactionModal({ ...transactionModal, [modal]: !transactionModal[modal] })
    }

    const [submitAccountsHistoryExport] = useSubmitAccountsHistoryExportMutation();
    const [isExporting, setIsExporting] = useState(false);

    /* Action Buttons */
    const transactionAction = row => {
        return <span style={{ whiteSpace: "nowrap" }}>
            <Button
                title="View Payment"
                className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                onClick={_ => setTransactionModal({ ...transactionModal, showViewTransactionModal: true, details: row })}
            >
                <span className="svg-icon svg-icon-md svg-icon-primary">
                    <SVG
                        title="View Transaction"
                        src={toAbsoluteUrl(
                            `/media/svg/icons/${(permissionAllow(adminUser, Permissions.TRANSACTIONS, PermissionType.UPDATE) && row.tranStatus === "pending") ? "Media/Play" : "General/Eye-2"}.svg`
                        )}
                    />
                </span>
            </Button>
        </span>
    };

    const formatActivity = row => {
        return <div className="d-flex align-items-center">
            <div className="symbol symbol-50 flex-shrink-0 mr-4">
                <span className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {transactionType(row)}
                    <span className="text-muted pl-2">
                        {transactionCat(row)}
                    </span>
                </span>
            </div>
        </div>
    }

    const buildTransactionsExportPayload = (filters = {}) => {
        const resolvedTranStatus = category === "pending-transaction"
            ? "pending"
            : (filters?.tranStatus || "");
        const resolvedTranType = category === "transfer-in"
            ? "intra_transfer_in"
            : (category === "transfer-out" ? "intra_transfer_out" : (filters?.tranType || ""));

        const payload = {
            userGroup,
            startDate: filters?.startDate || "",
            endDate: filters?.endDate || "",
            tranType: resolvedTranType,
            tranStatus: resolvedTranStatus,
            dateKey: "created",
            order: "desc",
            partners: filters?.partner || "",
        };

        return Object.entries(payload).reduce((accumulator, [key, value]) => {
            if (value === undefined || value === null || `${value}`.trim() === "") {
                return accumulator;
            }

            accumulator[key] = value;
            return accumulator;
        }, {});
    };

    const handleTransactionsExport = async (filters = {}) => {
        const exportPayload = buildTransactionsExportPayload(filters);

        try {
            setIsExporting(true);
            cogoToast.info("Submitting transactions export...");

            await submitAccountsHistoryExport(
                exportPayload
            ).unwrap();

            cogoToast.success("Transactions report will be sent to your email.");
        } catch (error) {
            cogoToast.error(getExportErrorMessage(error));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <FilterGroup
                key={`${userGroup}-${category}`}
                defaultFilters={{ "type": "live", endDate: new Date().toISOString().split('T')[0] }}
            >
                <FilterGroup.DateRange className={"mb-4"} />
                <div className="d-flex">
                    <div className={"mb-4 mr-4"}>
                        <FilterGroup.Filter className={"mr-4"} name={"type"} label={"Type"}>
                            <FilterGroup.Option value={"live"}>Live</FilterGroup.Option>
                            <FilterGroup.Option value={"demo"}>Demo</FilterGroup.Option>
                        </FilterGroup.Filter>
                    </div>
                    <div className={"mr-4"} >
                        {
                            type === "demo" ?
                                <FilterGroup.Filter className={"mr-4"} name={"partner"} label={"Partner"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    <FilterGroup.Option value={"paper_ngn"}>Paper NGN</FilterGroup.Option>
                                    <FilterGroup.Option value={"paper_usd"}>Paper USD</FilterGroup.Option>
                                </FilterGroup.Filter>
                                :
                                <FilterGroup.Filter className={"mr-4"} name={"partner"} label={"Partner"}>
                                    <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                    {
                                        PARTNERS.map(({name, value}) => (
                                            <FilterGroup.Option value={value}>{name}</FilterGroup.Option>
                                        ))
                                    }
                                </FilterGroup.Filter>
                        }
                    </div>
                    {
                        !["transfer-in", "transfer-out"].includes(category) &&
                        <div className={"mr-4"}>
                            <FilterGroup.Filter name={"tranType"} label={"Activity"}>
                                <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                <FilterGroup.Option value={"deposit"}>Deposits</FilterGroup.Option>
                                <FilterGroup.Option value={"withdraw"}>Withdrawals</FilterGroup.Option>
                                <FilterGroup.Option value={"PORTFOLIO_CREATION"}>Portfolio Creations</FilterGroup.Option>
                                <FilterGroup.Option value={"CARD_CREATION"}>Virtual Card Creations</FilterGroup.Option>
                                <FilterGroup.Option value={"CARD_DEPOSIT"}>Virtual Card Deposits</FilterGroup.Option>
                                <FilterGroup.Option value={"intra_transfer_in"}>Intra Transfer In</FilterGroup.Option>
                                <FilterGroup.Option value={"intra_transfer_out"}>Intra Transfer Out</FilterGroup.Option>
                                <FilterGroup.Option value={"INTER_TRANSFER_IN"}>Inter Transfer In</FilterGroup.Option>
                                <FilterGroup.Option value={"INTER_TRANSFER_OUT"}>Inter Transfer Out</FilterGroup.Option>
                            </FilterGroup.Filter>
                        </div>
                    }
                    <div className={"mr-4"}>
                        <FilterGroup.Filter name={"tranStatus"} label={"Status"}>
                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                            <FilterGroup.Option value={"pending"}>Pending</FilterGroup.Option>
                            <FilterGroup.Option value={"executed"}>Executed</FilterGroup.Option>
                            <FilterGroup.Option value={"failed"}>Failed</FilterGroup.Option>
                        </FilterGroup.Filter>
                    </div>
                    <div className={"mr-4"}>
                        <FilterGroup.Filter name={"limit"} label={"Limit"}>
                            <FilterGroup.Option value="">10</FilterGroup.Option>
                            <FilterGroup.Option value="20">20</FilterGroup.Option>
                            <FilterGroup.Option value="50">50</FilterGroup.Option>
                            <FilterGroup.Option value="100">100</FilterGroup.Option>
                        </FilterGroup.Filter>
                    </div>
                </div>
                <FilterGroup.Content>
                    {
                        ({ filters }) => {
                            return <Card>
                                <CardHeader title={`${ucFirst(filters?.type)} transactions list`}>
                                    <Permission permission={Permissions.REPORTS} permissionType={PermissionType.CREATE}>
                                        <CardHeaderToolbar>
                                            {/*
                                            <button
                                                type="button"
                                                className="btn btn-primary mr-4"
                                                disabled={isExporting}
                                                onClick={() => downloadTransactionsReport({
                                                    userGroup,
                                                    liveData: filters?.type === "live",
                                                    partner: filters?.partner || "",
                                                    startDate: filters?.startDate || "",
                                                    endDate: filters?.endDate || "",
                                                    status: category === "pending-transaction" ? "pending" : (filters?.tranStatus || ""),
                                                    type: category === "transfer-in" ? "intra_transfer_in" : (category === "transfer-out" ? "intra_transfer_out" : (filters?.tranType || "")),
                                                    format: "xlsx",
                                                })}
                                            >
                                                Export XLSX
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={isExporting}
                                                onClick={() => downloadTransactionsReport({
                                                    userGroup,
                                                    liveData: filters?.type === "live",
                                                    partner: filters?.partner || "",
                                                    startDate: filters?.startDate || "",
                                                    endDate: filters?.endDate || "",
                                                    status: category === "pending-transaction" ? "pending" : (filters?.tranStatus || ""),
                                                    type: category === "transfer-in" ? "intra_transfer_in" : (category === "transfer-out" ? "intra_transfer_out" : (filters?.tranType || "")),
                                                    format: "csv",
                                                })}
                                            >
                                                Export CSV
                                            </button>
                                            */}
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={isExporting}
                                                onClick={() => handleTransactionsExport(filters)}
                                            >
                                                {isExporting ? "Submitting..." : "Export Transactions"}
                                            </button>
                                        </CardHeaderToolbar>
                                    </Permission>
                                </CardHeader>
                                <CardBody>
                                    <DynamicSimpleTable
                                        key={`${userGroup}-${category}`}
                                        queryParams={
                                            `userGroup=${userGroup}`
                                            + `&limit=${filters?.limit || 10}`
                                            + `&liveData=${filters?.type === "live"}`
                                            + `&partner=${filters?.partner || ""}`
                                            + `&startDate=${filters?.startDate || ""}`
                                            + `&endDate=${filters?.endDate || ""}`
                                            + `&status=${category === "pending-transaction" ? "pending" : (filters?.tranStatus || "")}`
                                            + `&type=${category === "transfer-in" ? "intra_transfer_in" : (category === "transfer-out" ? "intra_transfer_out" : (filters?.tranType || ""))}`
                                        }
                                        searchBox={true}
                                        source={{ url: `/transactions` }}
                                        columns={[
                                            { title: "ACTIVITY", format: formatActivity },
                                            { title: "DATE", format: row => formatDate(row.created) },
                                            { title: "ID", key: "autoId" },
                                            { title: "REFERENCE", key: "refId" },
                                            { title: "DESCRIPTION", key: "comment" },
                                            { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "transactions") },
                                            { title: "AMOUNT", format: row => formatAmount(row.currency, row.tranAmount) },
                                            { title: "STATUS", format: row => formatStatus(ucFirst(row.tranStatus)) },
                                            { title: "SOURCE", key: "source" },
                                            { title: "DESTINATION", key: "destination" },
                                            { title: "PARTNER", format: row => ucFirst(row.partner) },
                                            { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, getTransactionBalanceBefore(row)) },
                                            { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getTransactionBalanceAfter(row)) },
                                            { title: "", format: transactionAction },
                                        ]}
                                        onError={(err) =>
                                            cogoToast.error(errorMessage(err))
                                        }
                                        refreshIcon={true}
                                        ref={tableRef}
                                    />
                                </CardBody>
                            </Card>
                        }
                    }
                </FilterGroup.Content>
            </FilterGroup>

            {transactionModal.showViewTransactionModal && <ViewTransactionModal {...transactionModal} onHide={toggleModal("showViewTransactionModal")} tableRef={tableRef} />}
        </>
    );
};
